import { useState, useEffect, useRef, FormEvent } from 'react';
import { SERVICE_SELECT_OPTIONS } from '../../../mocks/services';

interface Props {
  dark?: boolean;
  formAction: string;
  formId: string;
}

type Step = 1 | 2 | 3;

export default function ServiceForm({ dark = false, formAction, formId }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [service, setService] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [consentTimestamp, setConsentTimestamp] = useState('');
  const consentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const checkboxRef = useRef<HTMLInputElement>(null);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [rateLimited, setRateLimited] = useState(false);
  const lastSubmitTime = useRef<number>(0);
  const rateLimitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup rate limit timer on unmount (prevents memory leak)
  useEffect(() => {
    return () => {
      if (rateLimitTimer.current) clearTimeout(rateLimitTimer.current);
    };
  }, []);

  // Fetch IP with AbortController + 5s timeout for security
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch('https://api.ipify.org?format=json', { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => setIpAddress(typeof d.ip === 'string' ? d.ip.slice(0, 45) : ''))
      .catch(() => setIpAddress('unavailable'))
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  // Record exact timestamp when user reaches Step 3
  useEffect(() => {
    if (step === 3) {
      setConsentTimestamp(new Date().toISOString());
    }
  }, [step]);

  const inputClass = `w-full px-4 py-3 rounded-md border text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#FF6B00] ${
    dark
      ? 'bg-white/10 border-white/20 text-white placeholder-white/50'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  }`;

  const goStep2 = () => {
    if (!service) { setError('Please select a service.'); return; }
    setError('');
    setStep(2);
  };

  const goStep3 = () => {
    if (!zipcode || zipcode.length < 5) { setError('Please enter a valid 5-digit zip code.'); return; }
    setError('');
    setStep(3);
  };

  // Sanitize: strip HTML tags and limit length to prevent XSS injection in form payloads
  const sanitize = (val: string, maxLen = 200): string =>
    val.replace(/<[^>]*>/g, '').replace(/[<>"'`]/g, '').trim().slice(0, maxLen);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Rate limiting — max 3 attempts per 60 seconds
    const now = Date.now();
    if (rateLimited) {
      setError('Too many attempts. Please wait 60 seconds before trying again.');
      return;
    }
    const newAttempts = submitAttempts + 1;
    setSubmitAttempts(newAttempts);
    if (newAttempts >= 3 && now - lastSubmitTime.current < 60000) {
      setRateLimited(true);
      setError('Too many attempts. Please wait 60 seconds before trying again.');
      rateLimitTimer.current = setTimeout(() => {
        setRateLimited(false);
        setSubmitAttempts(0);
        setError('');
      }, 60000);
      return;
    }
    lastSubmitTime.current = now;

    // Sanitize all inputs before validation
    const safeName = sanitize(name, 100);
    const safePhone = sanitize(phone, 20);
    const safeEmail = sanitize(email, 150);
    const safeZipcode = zipcode.replace(/\D/g, '').slice(0, 5);
    const safeService = sanitize(service, 100);

    if (!safeName) { setError('Please enter your name.'); return; }

    const digitsOnly = safePhone.replace(/\D/g, '');
    if (!safePhone || digitsOnly.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!safeEmail || !emailRegex.test(safeEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!consentChecked) {
      setConsentError(true);
      checkboxRef.current?.focus();
      setError('You must agree to the consent terms before submitting.');
      return;
    }
    setError('');
    setConsentError(false);

    // Retrieve TrustedForm cert URL that the TrustedForm script auto-injects
    // NOTE: The hidden input MUST be uncontrolled (defaultValue) so TrustedForm
    // can set its value freely without React overriding it back to "".
    const tfCertField = document.querySelector<HTMLInputElement>('input[name="xxTrustedFormCertUrl"]');
    const trustedFormCertUrl = tfCertField?.value ?? '';

    const body = new URLSearchParams({
      Lead_Name: safeName,
      Lead_Phone: safePhone,
      email: safeEmail,
      Service_Type: safeService,
      Zip_Code: safeZipcode,
      // TCPA Proof Engine fields (stored for 4-year compliance)
      consent_given: 'true',
      consent_timestamp: consentTimestamp,
      consent_url: consentUrl.slice(0, 500),
      user_agent: userAgent.slice(0, 500),
      ip_address: ipAddress,
      trusted_form_cert_url: trustedFormCertUrl.slice(0, 500),
    });

    try {
      await fetch(formAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    }
  };

  const stepLabel = ['Select Service', 'Enter Zip Code', 'Your Info'];

  if (submitted) {
    return (
      <div className={`mt-6 rounded-xl p-8 text-center ${dark ? 'bg-white/10' : 'bg-[#F7F9FC]'}`}>
        <div className="w-14 h-14 flex items-center justify-center bg-[#10B981] rounded-full mx-auto mb-4">
          <i className="ri-check-line text-white text-2xl" />
        </div>
        <h3 className={`text-xl font-bold font-heading mb-2 ${dark ? 'text-white' : 'text-[#0A2540]'}`}>
          You&apos;re matched! A pro is on the way.
        </h3>
        <p className={`text-sm ${dark ? 'text-white/70' : 'text-gray-500'}`}>
          We&apos;ve received your request for <strong>{service}</strong> in <strong>{zipcode}</strong>. Expect a call within minutes.
        </p>
      </div>
    );
  }

  return (
    <form
      id={formId}
      data-readdy-form
      onSubmit={handleSubmit}
      className="mt-6"
    >
      {/* Hidden proof fields — captured for 4-year TCPA compliance storage */}
      {/*
        CRITICAL: xxTrustedFormCertUrl MUST use defaultValue (uncontrolled).
        Using value="" (controlled) causes React to override TrustedForm's injected
        cert URL on every re-render, meaning the cert is never captured in the payload.
      */}
      <input type="hidden" name="xxTrustedFormCertUrl" defaultValue="" />
      <input type="hidden" name="consent_timestamp" value={consentTimestamp} />
      <input type="hidden" name="consent_url" value={consentUrl} />
      <input type="hidden" name="user_agent" value={userAgent} />
      <input type="hidden" name="ip_address" value={ipAddress} />
      <input type="hidden" name="consent_given" value={consentChecked ? 'true' : 'false'} />

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-5">
        {([1, 2, 3] as Step[]).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                step === s
                  ? 'bg-[#FF6B00] text-white'
                  : step > s
                  ? 'bg-[#10B981] text-white'
                  : dark
                  ? 'bg-white/20 text-white/50'
                  : 'bg-gray-200 text-gray-400'
              }`}
            >
              {step > s ? <i className="ri-check-line text-xs" /> : s}
            </div>
            <span className={`text-xs hidden sm:block ${dark ? 'text-white/60' : 'text-gray-400'}`}>
              {stepLabel[s - 1]}
            </span>
            {s < 3 && <div className={`w-8 h-px ${dark ? 'bg-white/20' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Service */}
      {step === 1 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            id="service-select"
            name="service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            aria-label="Select service type"
            className={`${inputClass} flex-1`}
          >
            <option value="">Select Service Type...</option>
            {SERVICE_SELECT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={goStep2}
            className="bg-[#FF6B00] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            Next: Zip Code →
          </button>
        </div>
      )}

      {/* Step 2: Zip code */}
      {step === 2 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            id="zip-code"
            type="text"
            name="zip_code"
            value={zipcode}
            onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="Enter your zip code..."
            maxLength={5}
            autoComplete="postal-code"
            aria-label="Zip code"
            className={`${inputClass} flex-1`}
          />
          <button
            type="button"
            onClick={goStep3}
            className="bg-[#FF6B00] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            Find My Local Pro →
          </button>
        </div>
      )}

      {/* Step 3: Contact info + TCPA Consent */}
      {step === 3 && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label htmlFor="lead-name" className="sr-only">Full name</label>
              <input
                id="lead-name"
                type="text"
                name="Lead_Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
                maxLength={100}
                className={`${inputClass} w-full`}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="lead-phone" className="sr-only">Phone number</label>
              <input
                id="lead-phone"
                type="tel"
                name="Lead_Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone number"
                autoComplete="tel"
                maxLength={20}
                className={`${inputClass} w-full`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="lead-email" className="sr-only">Email address</label>
            <input
              id="lead-email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              autoComplete="email"
              maxLength={150}
              className={inputClass}
            />
          </div>
          <input type="hidden" name="Service_Type" value={service} />
          <input type="hidden" name="Zip_Code" value={zipcode} />

          {/* ================================================================
              TCPA / CPA / DNC CONSENT BLOCK — MD 6 Compliant
              • Unchecked by default (user must physically click)
              • "Clear and Conspicuous" — 14px min, above submit button
              • Exact opt-in copy per MD 6 Section 2
              ================================================================ */}
          <div
            className={`rounded-lg border-2 p-4 transition-colors ${
              consentError
                ? 'border-red-500 bg-red-500/10'
                : dark
                ? 'border-white/20 bg-white/5'
                : 'border-gray-300 bg-[#F7F9FC]'
            }`}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              {/* Mandatory unchecked checkbox — cannot be pre-filled */}
              <input
                ref={checkboxRef}
                type="checkbox"
                name="tcpa_consent"
                checked={consentChecked}
                onChange={(e) => {
                  setConsentChecked(e.target.checked);
                  if (e.target.checked) setConsentError(false);
                }}
                required
                className="mt-1 h-5 w-5 min-w-[20px] rounded border-2 border-gray-400 accent-[#FF6B00] cursor-pointer"
              />
              {/* Exact TCPA opt-in copy per MD 6 Section 2 — 14px, above submit */}
              <span
                className={`leading-relaxed select-none ${dark ? 'text-white/80' : 'text-gray-700'}`}
                style={{ fontSize: '14px', lineHeight: '1.65' }}
              >
                I agree to the{' '}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-[#FF6B00] transition-colors"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-[#FF6B00] transition-colors"
                >
                  Privacy Policy
                </a>
                . By checking this box, I provide my prior express written consent authorizing{' '}
                <strong>HomeProDispatch.com</strong> and its network of up to{' '}
                <strong>4 home service providers</strong> to contact me at the phone number and email
                address provided above with offers and information about their products and services. I
                understand and agree that these communications may be delivered via telephone calls,
                emails, artificial or pre-recorded voice messages, and SMS text messages, and may be
                generated using automated telephone technology or an automatic dialing system. I authorize
                these communications even if my telephone number is currently listed on any corporate,
                state, or federal{' '}
                <strong>Do Not Call (DNC) list</strong>. I understand that my consent is not a condition
                of purchasing any property, goods, or services, and that I may revoke this consent at any
                time (e.g., by replying <strong>STOP</strong> to a text message). Message and data rates
                may apply.{' '}
                <a
                  href="/california-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-[#FF6B00] transition-colors"
                >
                  California Notice
                </a>
                .
              </span>
            </label>

            {consentError && (
              <p className="mt-2 text-red-500 text-sm font-semibold flex items-center gap-1.5">
                <i className="ri-error-warning-fill" />
                You must check this box to continue. Your consent is required.
              </p>
            )}
          </div>

          {/* Submit button — below consent block per "directly above" = consent is directly above button */}
          <button
            type="submit"
            disabled={!consentChecked || rateLimited}
            className={`font-bold px-8 py-4 rounded-md text-base transition-all whitespace-nowrap cursor-pointer w-full ${
              consentChecked && !rateLimited
                ? 'bg-[#FF6B00] hover:bg-orange-600 text-white shadow-md'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60'
            }`}
          >
            {rateLimited ? 'Please wait 60 seconds...' : 'Get My Free Quote Now →'}
          </button>

          {/* Micro-trust line below button */}
          <p
            className={`text-center ${dark ? 'text-white/40' : 'text-gray-400'}`}
            style={{ fontSize: '12px' }}
          >
            <i className="ri-lock-fill mr-1" />
            SSL Encrypted &nbsp;·&nbsp; No spam &nbsp;·&nbsp; Consent recorded &amp; certified
          </p>
        </div>
      )}

      {error && (
        <p
          role="alert"
          aria-live="assertive"
          className="mt-2 text-red-400 text-sm font-medium flex items-center gap-1.5"
        >
          <i className="ri-error-warning-fill" />
          {error}
        </p>
      )}
    </form>
  );
}
